import React, { useState, useEffect } from "react";
import {
  Container, TextField, Button, Typography, Box, FormControl,
  InputLabel, Select, MenuItem
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { variantsAPI, productsAPI } from "../../services/api";
import BackToDashboard from "../common/BackToAdminDashboard";
import Autocomplete from "@mui/material/Autocomplete";

const UpdateVariant = () => {
  const { t } = useTranslation();
  const [products, setProducts] = useState([]);
  const [variants, setVariants] = useState([]);
  const [selectedProductId, setSelectedProductId] = useState("");
  const [filteredVariants, setFilteredVariants] = useState([]);
  const [selectedVariantId, setSelectedVariantId] = useState("");
  const [selectedVariant, setSelectedVariant] = useState(null);

  const [form, setForm] = useState({
    product_id: "",
    brand_name_en: "",
    brand_name_el: "",
    price: "",
    unit_en: "",
    unit_el: ""
  });
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  // Φέρνουμε όλα τα προϊόντα & όλα τα variants
  useEffect(() => {
    productsAPI.list().then(res => setProducts(res.data || []));
    variantsAPI.list().then(res => setVariants(res.data || []));
  }, []);

  // Φιλτράρουμε variants ανά προϊόν
  useEffect(() => {
    if (selectedProductId) {
      const filtered = variants.filter(v => String(v.product_id) === String(selectedProductId));
      setFilteredVariants(filtered);
      setSelectedVariantId("");
      setSelectedVariant(null);
      setForm({
        product_id: selectedProductId,
        brand_name_en: "",
        brand_name_el: "",
        price: "",
        unit_en: "",
        unit_el: ""
      });
    } else {
      setFilteredVariants([]);
      setSelectedVariantId("");
      setSelectedVariant(null);
    }
  }, [selectedProductId, variants]);

  // Όταν διαλέξεις variant, εμφανίζει το form για επεξεργασία
  useEffect(() => {
    if (selectedVariantId) {
      const variant = filteredVariants.find(v => String(v.id) === String(selectedVariantId));
      setSelectedVariant(variant);
      setForm({
        product_id: variant.product_id,
        brand_name_en: variant.brand_name_en,
        brand_name_el: variant.brand_name_el,
        price: variant.price,
        unit_en: variant.unit_en || "",
        unit_el: variant.unit_el || ""
      });
    }
  }, [selectedVariantId, filteredVariants]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await variantsAPI.update(selectedVariant.id, {
        ...form,
        price: parseFloat(form.price)
      });
      navigate("/admin/dashboard");
    } catch (err) {
      setError(err.response?.data?.error || t("failed_to_update_variant"));
    }
  };

  return (
    <Container sx={{ mt: 4 }}>
      <BackToDashboard dashboardPath="/admin/dashboard" />
      <Typography variant="h4" gutterBottom>
        {t("update_variant")}
      </Typography>
      {error && <Typography color="error">{error}</Typography>}

      {/* Επιλογή Προϊόντος */}
      <FormControl fullWidth sx={{ mb: 3 }}>
        <InputLabel>{t("select_product")}</InputLabel>
        <Autocomplete
          options={products}
          getOptionLabel={(option) => option.name_en}
          filterOptions={(options, { inputValue }) =>
            options.filter(option =>
              option.name_en.toLowerCase().includes(inputValue.toLowerCase())
            )
          }
          value={products.find(p => String(p.id) === String(selectedProductId)) || null}
          onChange={(event, newValue) => {
            setSelectedProductId(newValue ? newValue.id : "");
          }}
          renderInput={(params) => (
            <TextField {...params} label={t("select_product")} variant="outlined" />
          )}
          sx={{ mb: 3 }}
        />

      </FormControl>

      {/* Επιλογή Variant του προϊόντος */}
      {filteredVariants.length > 0 && (
        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel>{t("select_variant")}</InputLabel>
          <Select
            value={selectedVariantId}
            onChange={e => setSelectedVariantId(e.target.value)}
          >
            {filteredVariants.map((v) => (
              <MenuItem key={v.id} value={v.id}>
                {v.brand_name_en} - {v.price}€
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {/* Form Επεξεργασίας Variant */}
      {selectedVariant && (
        <form onSubmit={handleSubmit}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {/* Το προϊόν (readonly, μιας και το αλλάζεις από πάνω) */}
            <TextField
              label={t("product")}
              value={products.find(p => p.id === selectedVariant.product_id)?.name_en || ""}
              disabled
              fullWidth
            />
            <TextField
              label={t("brand_name_en")}
              name="brand_name_en"
              fullWidth
              required
              value={form.brand_name_en}
              onChange={handleChange}
            />
            <TextField
              label={t("brand_name_el")}
              name="brand_name_el"
              fullWidth
              required
              value={form.brand_name_el}
              onChange={handleChange}
            />
            <TextField
              label={t("price")}
              name="price"
              type="number"
              fullWidth
              required
              value={form.price}
              onChange={handleChange}
            />
            <TextField
              label={t("unit_en")}
              name="unit_en"
              fullWidth
              value={form.unit_en}
              onChange={handleChange}
            />
            <TextField
              label={t("unit_el")}
              name="unit_el"
              fullWidth
              value={form.unit_el}
              onChange={handleChange}
            />
            <Button type="submit" variant="contained" color="primary">
              {t("update_variant")}
            </Button>
          </Box>
        </form>
      )}
    </Container>
  );
};

export default UpdateVariant;
